import { memo, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import DigitransLoader from './DigitransLoader';
import { isVisibleItem, phaseLabel } from '../hooks/messageStream';
import { useTheme } from '../hooks/useTheme';
import { AlertIcon, BrainIcon, CheckIcon, ChevronIcon, RefreshIcon, StopIcon } from './icons';
import { OpenArtifactButton } from './artifacts/ArtifactContext';
import { effortSummary, effortTooltip } from '../utils/effortPreference.js';
import { analyseEssais, formatResume, libelleAffiche, parametresRestants, resumeActions } from '../utils/stepResume.js';
import { formatStepProgress } from '../utils/stepProgress.js';
import './ReasoningPanel.css';

// ── Formats ─────────────────────────────────────────────────────────
const nf1 = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 1, minimumFractionDigits: 1 });

/** Durée lisible : « 0,4 s », « 12 s », « 1 min 05 s ». */
export function formatDuration(ms, { precise = true } = {}) {
  if (ms == null || Number.isNaN(ms)) return '';
  const seconds = Math.max(0, ms) / 1000;
  if (seconds < 10 && precise) return `${nf1.format(seconds)} s`;
  if (seconds < 60) return `${Math.floor(seconds)} s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} min ${String(Math.floor(seconds % 60)).padStart(2, '0')} s`;
}

// Temps écoulé, rafraîchi tant que la réflexion est en cours
export function useElapsed(startedAt, endedAt, running) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [running]);
  if (!startedAt) return 0;
  return (endedAt ?? (running ? now : startedAt)) - startedAt;
}

// ── Éléments de la chronologie ──────────────────────────────────────
// Échec corrigé par un nouvel essai du même outil : marqueur neutre (flèche de reprise).
// Étape annoncée dont le modèle écrit encore les arguments (step_pending) : point pulsé ; le
// spinner ne tourne que pendant l'exécution, seule mesurée par la durée finale (duree_ms)
function StepMarker({ status, corrigee = false, pending = false }) {
  if (status === 'running' && pending) return <span className="reasoning-preparation" aria-hidden="true" />;
  if (status === 'running') return <span className="reasoning-spinner" aria-hidden="true" />;
  if (status === 'ok') return <CheckIcon size={12} strokeWidth={2.6} />;
  if (status === 'error') return corrigee ? <RefreshIcon size={12} /> : <AlertIcon size={13} />;
  return <StopIcon size={13} />;
}

const STEP_STATUS_LABEL = {
  running: 'en cours',
  ok: 'terminée',
  error: 'en erreur',
  cancelled: 'interrompue',
};

// Valeurs simples des paramètres (période d'un résumé identique aux dates demandées : omise)
const valeursParams = (params) =>
  params && typeof params === 'object' ? Object.values(params).filter((v) => v != null && typeof v !== 'object').join(' ') : String(params ?? '');

// Étape en cours (contrat v1.8, MCP-13) : compteur de secondes à partir du début réel de
// l'exécution (step_progress, file d'attente du serveur exclue), affiché après 1 s ; avancement
// « étape n/N » et barre fine des outils qui le transmettent. Les deux disparaissent à la fin
// de l'étape (durée finale) ; ni l'un ni l'autre n'est enregistré. Seule l'annonce aria-live du
// panneau parle aux lecteurs d'écran (compteur et texte masqués), la barre reste consultable :
// nommée d'après son étape (plusieurs outils longs dans le même tour), décrite par la ligne des
// paramètres quand elle existe (même outil appelé deux fois avec le même libellé).
// Étapes concises (lot 2) : une ligne « Alertes · freinage brusque · 4631-KT-01 » (libellé court
// du serveur, qui porte ses paramètres), « … » pendant l'action, « — 128 résultats » à la fin,
// « (2e essai) » après un échec du même outil sur le même sujet. La ligne des paramètres ne garde
// que ceux absents du libellé (libellés historiques « Je récupère… » de l'historique). Un échec
// corrigé ensuite reste lisible, en neutre ; seul l'échec final est rouge. Étape annoncée
// (step_pending) dont le modèle écrit encore les arguments : « — préparation… » jusqu'au step_start.
function StepItem({ item, essai = 1, corrigee = false }) {
  const running = item.status === 'running';
  const preparation = running && Boolean(item.pending);
  const params = parametresRestants(item.label, item.params);
  const resume = formatResume(item.resume, valeursParams(item.params));
  const metaId = useId();
  const execStartedAt = running && Number.isFinite(item.execStartedAt) ? item.execStartedAt : null;
  const ecoule = useElapsed(execStartedAt, null, execStartedAt != null);
  const progress = running ? formatStepProgress(item.progress) : null;
  const statut = corrigee ? 'corrigée par un nouvel essai' : preparation ? 'en préparation' : STEP_STATUS_LABEL[item.status];
  return (
    <>
      <span className={`reasoning-marker reasoning-marker--step is-${item.status}${corrigee ? ' is-corrigee' : ''}`}>
        <StepMarker status={item.status} corrigee={corrigee} pending={preparation} />
      </span>
      <div className="reasoning-item-body">
        <div className="reasoning-step-line">
          <span className="reasoning-step-label">
            {libelleAffiche(item.label || item.tool, running && !preparation)}
            {preparation && <span className="reasoning-step-resume">{' — préparation…'}</span>}
            {essai > 1 && <span className="reasoning-step-essai">{` (${essai}e essai)`}</span>}
            {resume && <span className="reasoning-step-resume">{` — ${resume}`}</span>}
          </span>
          <span className="sr-only">, étape {statut}</span>
          {execStartedAt != null && ecoule >= 1000 && (
            <span className="reasoning-step-elapsed" aria-hidden="true">{formatDuration(ecoule, { precise: false })}</span>
          )}
          {!running && item.duree_ms != null && (
            <span className="reasoning-step-duration">{formatDuration(item.duree_ms)}</span>
          )}
        </div>
        {progress && (
          <div className="reasoning-step-progress">
            <span className="reasoning-step-progress__text" aria-hidden="true">{progress.texte}</span>
            <div
              className="reasoning-step-progress__track"
              role="progressbar"
              aria-label={item.label ? `Avancement de l'étape : ${item.label}` : "Avancement de l'étape"}
              aria-describedby={params ? metaId : undefined}
              aria-valuemin={0}
              aria-valuemax={item.progress.N}
              aria-valuenow={item.progress.n}
              aria-valuetext={progress.valeurTexte}
            >
              <span className="reasoning-step-progress__fill" style={{ width: `${progress.pourcentage}%` }} />
            </div>
          </div>
        )}
        {params && (
          <div className="reasoning-step-meta" id={metaId}>
            {params}
          </div>
        )}
        {item.status === 'error' && (
          <div className={`reasoning-step-error${corrigee ? ' is-corrigee' : ''}`}>{item.error || 'Cette étape a échoué.'}</div>
        )}
      </div>
    </>
  );
}

// Réflexion en direct : la zone n'en montre que les dernières lignes, seule la fin du texte est
// rendue. Une longue réflexion (Qwen : plusieurs dizaines de milliers de caractères, en anglais)
// n'est pas remise en page en entier à chaque rafraîchissement du flux ; le texte complet revient
// dès la fin de l'écriture (panneau déplié, panneau d'artefacts). Coupe sur un blanc proche.
export const LIVE_TAIL_CHARS = 1500;
export function liveTail(text) {
  if (text.length <= LIVE_TAIL_CHARS) return text;
  const tail = text.slice(-LIVE_TAIL_CHARS);
  const blank = tail.search(/\s/);
  return blank >= 0 && blank < 120 ? tail.slice(blank + 1) : tail;
}

// Mémorisé : seul l'élément qui change (même objet sinon, messageStream.js) est rendu à nouveau.
// `compact` (réponse en cours) : tous les blocs gardent la hauteur de la zone en direct, calés sur
// la fin du texte ; un bloc qui cesse d'être le dernier (étape qui suit) ne grandit donc pas et
// ne fait pas sauter l'écran. Fini, il garde son texte entier, consultable en remontant dans la
// zone ; la hauteur pleine revient à la fin de la réponse (panneau déplié) et dans le panneau
// d'artefacts.
const ThinkingItem = memo(function ThinkingItem({ item, live, compact = live }) {
  const ref = useRef(null);
  const [overflowing, setOverflowing] = useState(false);

  // Défilement automatique vers la fin du texte (en cours d'écriture, ou passé de la fin rendue
  // au texte entier) ; le fondu du haut n'apparaît que si le texte dépasse la zone
  useLayoutEffect(() => {
    const el = ref.current;
    if (!compact || !el) return;
    el.scrollTop = el.scrollHeight;
    setOverflowing(el.scrollHeight > el.clientHeight + 1);
  }, [item.text, compact, live]);

  return (
    <>
      <span className="reasoning-marker reasoning-marker--thinking" aria-hidden="true" />
      <div className="reasoning-item-body">
        <div
          ref={ref}
          className={`reasoning-thinking ${compact ? 'is-compact' : ''} ${live ? 'is-live' : ''} ${compact && overflowing ? 'is-overflowing' : ''}`}
        >
          {(live ? liveTail(item.text) : item.text).trim()}
        </div>
      </div>
    </>
  );
});

function NarrationItem({ item }) {
  return (
    <>
      <span className="reasoning-marker reasoning-marker--narration" aria-hidden="true" />
      <div className="reasoning-item-body">
        <p className="reasoning-narration">{item.text}</p>
      </div>
    </>
  );
}

/**
 * Chronologie du raisonnement (panneau replié dans le message, ou panneau d'artefacts).
 * `full` : réflexion affichée en entier, sans hauteur limitée.
 */
export function ReasoningTimeline({ items, isRunning = false, full = false }) {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.36, ease: [0.2, 0, 0, 1] };
  const lastItem = items[items.length - 1];
  // Nouveaux essais d'un outil après un échec (« 2e essai ») et échecs corrigés (neutres)
  const essais = analyseEssais(items);
  return (
    <ol className={`reasoning-timeline ${full ? 'reasoning-timeline--full' : ''}`}>
      {items.map((item) => (
        <motion.li
          key={item.key}
          className={`reasoning-item reasoning-item--${item.type}`}
          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          transition={transition}
        >
          {item.type === 'step' && <StepItem item={item} essai={essais.get(item.key)?.essai} corrigee={essais.get(item.key)?.corrigee} />}
          {item.type === 'narration' && <NarrationItem item={item} />}
          {item.type === 'thinking' && (
            <ThinkingItem item={item} live={!full && isRunning && item === lastItem} compact={!full && isRunning} />
          )}
        </motion.li>
      ))}
    </ol>
  );
}

/**
 * En-tête de fin d'un raisonnement : « Raisonnement · 1 min 12 s · 3 recherches · 2 visuels »,
 * « Raisonnement interrompu après 42 s » (Stop ou erreur) ; actions comptées sans les échecs
 * corrigés par un nouvel essai (resumeActions). La durée est celle du raisonnement : l'en-tête
 * s'affiche dès que la réponse s'écrit, il ne dit donc jamais « Terminé ». Avec le niveau
 * d'effort appliqué (sprint 6) : « … · Approfondi (auto) », et la mention d'un modèle sans réflexion.
 */
export function reasoningSummary(reasoning, elapsed, effort = null) {
  const duree = elapsed != null ? formatDuration(elapsed, { precise: false }) : '';
  const interrompu = reasoning.status === 'stopped' || reasoning.status === 'error';
  const tete = interrompu
    ? (duree ? `Raisonnement interrompu après ${duree}` : 'Raisonnement interrompu')
    : duree ? `Raisonnement · ${duree}` : 'Raisonnement';
  return [
    tete,
    resumeActions(reasoning.items.filter(isVisibleItem)) || null,
    effortSummary(effort) || null,
  ].filter(Boolean).join(' · ');
}

/** Niveau d'effort du résumé, avec la raison du choix automatique en info-bulle. */
function EffortBadge({ effort, className = '' }) {
  const text = effortSummary(effort);
  if (!text) return null;
  const tooltip = effortTooltip(effort);
  return (
    <span className={`reasoning-effort ${className}`.trim()} title={tooltip || undefined}>
      {text}
      {tooltip && tooltip !== text && <span className="sr-only">{` (${tooltip})`}</span>}
    </span>
  );
}

/**
 * Panneau « Raisonnement » : étapes, phrases de l'assistant et réflexion résumée,
 * dans l'ordre chronologique. Replié automatiquement à la fin de la réponse.
 * `messageId` : permet d'ouvrir la chronologie complète dans le panneau d'artefacts.
 * Lot 1 (vitesse) : `phase` (messageStream.js) donne le libellé de l'en-tête pendant le flux ;
 * `pending` : réponse partie, rien de visible encore. L'en-tête s'affiche alors seul (logo déjà
 * tracé, « Analyse de votre demande… », chronomètre depuis le clic), dès la première image ; le
 * même en-tête et le même loader restent en place quand la chronologie arrive.
 */
export const ReasoningPanel = memo(function ReasoningPanel({ reasoning, messageId = null, effort = null, phase = null, pending = false }) {
  const reduceMotion = useReducedMotion();
  const { resolved: theme } = useTheme();
  const bodyId = useId();
  const items = reasoning.items.filter(isVisibleItem);
  const isRunning = reasoning.status === 'running' || pending;
  const [manualOpen, setManualOpen] = useState(null);
  const expanded = manualOpen ?? isRunning;
  const elapsed = useElapsed(reasoning.startedAt, reasoning.endedAt, isRunning);

  if (!items.length && !pending) return null;
  // En-tête seul (aucun élément encore) : rien à déplier ni à ouvrir dans le panneau
  const hasItems = items.length > 0;

  const steps = items.filter((item) => item.type === 'step');
  const lastStep = steps[steps.length - 1];
  // En-tête pendant le flux : la phase du serveur ; sans phase (effacée par une étape), l'action
  // en cours (étape annoncée dont le modèle écrit les arguments, puis exécutée), sinon la réflexion
  const actionEnCours = [...steps].reverse().find((item) => item.status === 'running');
  const titreEnCours = phaseLabel(phase)
    ?? (actionEnCours ? (actionEnCours.pending ? "Préparation de l'action…" : 'Action en cours…') : 'Réflexion en cours…');
  // Niveau d'effort : affiché à part (info-bulle), hors du texte du résumé
  const summary = reasoningSummary(reasoning, elapsed);

  // Durée et courbe alignées sur les tokens --ds-dur-slow / --ds-ease-standard
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.36, ease: [0.2, 0, 0, 1] };
  const artifactId = messageId ? `${messageId}:reasoning` : null;

  return (
    <section className={`reasoning is-${reasoning.status}`} aria-label="Raisonnement de l'assistant">
      <div className="reasoning-head">
        <button
          type="button"
          className="reasoning-header"
          aria-expanded={hasItems ? expanded : undefined}
          aria-controls={hasItems ? bodyId : undefined}
          disabled={!hasItems}
          onClick={() => setManualOpen(!expanded)}
        >
          <span className="reasoning-indicator">
            {isRunning ? (
              <DigitransLoader size={28} theme={theme} label="Réflexion en cours" drawn />
            ) : (
              <BrainIcon size={16} />
            )}
          </span>
          {isRunning ? (
            <>
              <span className="reasoning-title is-shimmering">{titreEnCours}</span>
              <span className="reasoning-timer" aria-hidden="true">
                {formatDuration(elapsed, { precise: false })}
              </span>
              <EffortBadge effort={effort} className="is-running" />
            </>
          ) : (
            <span className="reasoning-title">
              {summary}
              {effortSummary(effort) && <span aria-hidden="true">{' · '}</span>}
              <EffortBadge effort={effort} />
            </span>
          )}
          {hasItems && <ChevronIcon size={16} className={`reasoning-chevron ${expanded ? 'is-open' : ''}`} />}
        </button>
        {artifactId && hasItems && (
          <OpenArtifactButton
            artifactId={artifactId}
            label="Ouvrir le raisonnement complet dans le panneau"
            className="reasoning-open"
            build={() => ({
              id: artifactId,
              type: 'reasoning',
              title: 'Raisonnement',
              messageId,
              payload: { messageId },
            })}
          />
        )}
      </div>

      {/* Annonce discrète de l'étape en cours pour les lecteurs d'écran */}
      <span className="sr-only" aria-live="polite">
        {isRunning && lastStep?.status === 'running' ? lastStep.label : ''}
      </span>

      <AnimatePresence initial={false}>
        {expanded && hasItems && (
          <motion.div
            id={bodyId}
            key="body"
            className="reasoning-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={transition}
          >
            <ReasoningTimeline items={items} isRunning={isRunning} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
});
